// projectlab-web/src/lib/supabase.ts
import { createClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database.types'
import { createLogger } from './logger'

const log = createLogger('Supabase')

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined
const appUrl = import.meta.env.VITE_APP_URL as string | undefined

if (!supabaseUrl) {
  log.error('VITE_SUPABASE_URL is not set. Check your .env.local file.')
  throw new Error('[ProjectLab] Missing VITE_SUPABASE_URL environment variable.')
}

if (!supabaseAnonKey) {
  log.error('VITE_SUPABASE_ANON_KEY is not set. Check your .env.local file.')
  throw new Error('[ProjectLab] Missing VITE_SUPABASE_ANON_KEY environment variable.')
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false, // AuthConfirmPage handles URL manually — prevents existing session hijack
    flowType: 'implicit',      // Emails use #access_token= hash — works on any device
    storageKey: 'projectlab:auth',
  },
  global: {
    headers: {
      'x-app-name': 'projectlab-web',
      'x-app-version': '0.1.0',
    },
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
})

supabase.auth.onAuthStateChange((event, session) => {
  log.debug('Auth state changed', {
    event,
    userId: session?.user?.id ?? null,
    userEmail: session?.user?.email ?? null,
    expiresAt: session?.expires_at ?? null,
  })
})

export function getAppUrl(): string {
  if (appUrl?.trim()) return appUrl.trim().replace(/\/+$/, '')
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }
  return ''
}

export function getAuthConfirmUrl(): string {
  return `${getAppUrl()}/auth/confirm`
}

export async function checkEmailConfirmationStatus(
  email: string,
): Promise<{
  exists: boolean
  emailConfirmed: boolean
  emailConfirmedAt: string | null
  userId: string | null
}> {
  const cleanEmail = email.trim().toLowerCase()

  const { data, error } = await supabase.functions.invoke('auth-confirmation-status', {
    body: { email: cleanEmail },
  })

  if (error) {
    logSupabaseError('checkEmailConfirmationStatus', error)
    throw error
  }

  return {
    exists: !!data?.exists,
    emailConfirmed: !!data?.emailConfirmed,
    emailConfirmedAt: data?.emailConfirmedAt ?? null,
    userId: data?.userId ?? null,
  }
}

export function logSupabaseError(context: string, error: unknown): void {
  if (error && typeof error === 'object' && 'message' in error) {
    log.error(`Supabase error in [${context}]`, {
      message: (error as { message: string }).message,
      details: (error as Record<string, unknown>).details ?? null,
      hint: (error as Record<string, unknown>).hint ?? null,
      code: (error as Record<string, unknown>).code ?? null,
    })
  } else {
    log.error(`Supabase error in [${context}]`, error)
  }
}

export async function isUniversityEmail(
  email: string,
): Promise<{ isUniversity: boolean; universityName?: string }> {
  const domain = email.split('@')[1]?.toLowerCase()
  if (!domain) return { isUniversity: false }

  const { data, error } = await supabase
    .from('student_emails')
    .select('university_name')
    .eq('domain', domain)
    .maybeSingle()

  if (error) {
    log.warn('University domain check failed', error)
    return { isUniversity: false }
  }

  return {
    isUniversity: !!data,
    universityName: data?.university_name ?? undefined,
  }
}

export async function uploadFile(
  bucket: 'cvs' | 'cover-letters' | 'br-documents' | 'avatars' | 'evidence',
  path: string,
  file: File,
): Promise<string> {
  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    cacheControl: '3600',
    upsert: true,
  })

  if (error) {
    log.error('File upload failed', { bucket, path, error })
    throw new Error(`Upload failed: ${error.message}`)
  }

  const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path)
  return urlData.publicUrl
}
